/**
 * A custom element that displays a horizontal bar reflecting how far the
 * visitor has scrolled down the document, similar to a reading progress
 * indicator on a blog post.
 */
class ReadingProgressBar extends HTMLElement {
  /** @type {number | null} */
  #rafId = null;

  /** @type {HTMLElement | null} */
  #fill = null;

  connectedCallback() {
    this.#fill = this.querySelector('.reading-progress-bar__fill');

    this.#update();
    window.addEventListener('scroll', this.#scheduleUpdate, { passive: true });
    window.addEventListener('resize', this.#scheduleUpdate, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.#scheduleUpdate);
    window.removeEventListener('resize', this.#scheduleUpdate);

    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }

  #scheduleUpdate = () => {
    // Throttle updates with requestAnimationFrame to avoid layout thrashing
    if (this.#rafId !== null) return;

    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#update();
    });
  };

  #update = () => {
    if (!this.#fill) return;

    const scrollableHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
    const progress = scrollableHeight > 0 ? (window.scrollY / scrollableHeight) * 100 : 0;
    const clampedProgress = Math.min(100, Math.max(0, progress));

    this.#fill.style.width = `${clampedProgress}%`;
    this.#fill.setAttribute('aria-valuenow', `${Math.round(clampedProgress)}`);
    // Hide the bar again once scrolled back to the very top.
    this.toggleAttribute('data-scrolled', window.scrollY > 0);
  };
}

if (!customElements.get('reading-progress-bar')) {
  customElements.define('reading-progress-bar', ReadingProgressBar);
}
